import { useEffect, useMemo } from "react";
import { toAbsoluteUrl } from "@/lib/utils/seo";
import { applyWechatShareData, ensureWechatJssdkConfigured } from "@/lib/client/wechat-jssdk";
import { isWeChatBrowser } from "@/lib/utils/wechat";

function getCurrentPageUrl(): string {
  if (typeof window === "undefined") {
    return "";
  }
  return window.location.href.split("#")[0] ?? "";
}

type UseWechatShareOptions = {
  enabled: boolean;
  title: string;
  desc: string;
  imgUrl?: string;
};

export function useWechatShare({ enabled, title, desc, imgUrl }: UseWechatShareOptions) {
  const currentPageUrl = getCurrentPageUrl() || "/";

  const normalized = useMemo(() => {
    return {
      title: title.trim().slice(0, 120),
      desc: desc.trim().slice(0, 300),
      link: toAbsoluteUrl(currentPageUrl),
      imgUrl: imgUrl ? toAbsoluteUrl(imgUrl) : toAbsoluteUrl("/favicon.png"),
    } as const;
  }, [title, desc, imgUrl, currentPageUrl]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !isWeChatBrowser()) {
      return;
    }

    const abortController = new AbortController();

    (async () => {
      try {
        await ensureWechatJssdkConfigured(currentPageUrl, {
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        if (!window.wx) {
          throw new Error("微信 JSSDK 未成功注入");
        }

        const sharePayload = {
          title: normalized.title,
          desc: normalized.desc,
          link: normalized.link,
          imgUrl: normalized.imgUrl,
        };
        applyWechatShareData(sharePayload);
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error("微信分享配置失败", error);
      }
    })();

    return () => {
      abortController.abort("组件卸载");
    };
  }, [enabled, normalized, currentPageUrl]);
}
